import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Radio, Paper, Chip } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PaypalVaultSection from "./PaypalVaultSection";
import ReCAPTCHA from "react-google-recaptcha";
import Spinner from "../element/BePatient";
import toast from "react-hot-toast";

export default function SavePaymentMethodForm({ onPaymentSaved, onClose, setOpenCardDialog }) {
  const [card, setCard] = useState(null);
  const [paypalAccount, setPaypalAccount] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("card");
  const [paypalError, setPaypalError] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [hasExistingPayment, setHasExistingPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const detectCardType = (number) => {
    const patterns = {
      visa: /^4/,
      mastercard: /^5[1-5]/,
      amex: /^3[47]/,
      discover: /^6(?:011|5)/,
    };

    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(number)) {
        return { name: type };
      }
    }
    return null;
  };

  const fetchPaymentMethods = async () => {
    setIsLoading(true);
    try {
      const user_id = localStorage.getItem("logged_user_id");
      const cardRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/fetch_card_from_vault`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id }),
        }
      );
      const cardData = await cardRes.json();

      const paypalRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/getCustomerEmail`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id }),
        }
      );
      const paypalData = await paypalRes.json();

      if (cardData?.status && (cardData?.card || cardData?.data)) {
        setCard(cardData.card || cardData.data);
        setPaypalAccount(null);
        setHasExistingPayment(true);
        setSelectedMethod("card");
      } else if (paypalData?.status && paypalData?.email) {
        setPaypalAccount({
          email: paypalData.email,
          name: paypalData.name?.full_name,
        });
        setCard(null);
        setHasExistingPayment(true);
        setSelectedMethod("paypal");
      } else {
        setCard(null);
        setPaypalAccount(null);
        setHasExistingPayment(false);
        setSelectedMethod("card"); // Default to card if no existing payments
        formik.resetForm();
      }
    } catch (error) {
      formik.resetForm();
      setCard(null);
      setPaypalAccount(null);
      setHasExistingPayment(false);
      setSelectedMethod("card");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!window.confirm("Are you sure you want to delete Payment Method?")) return;

    if (setOpenCardDialog) {
      setOpenCardDialog(false);
    }

    setIsLoading(true);

    try {
      const user_id = localStorage.getItem("logged_user_id");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/delete_payment_token`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id }),
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success("Your Payment Method has been Deleted successfully from Chef Republic");
        setPaypalAccount(null);
        setCard(null);
        setHasExistingPayment(false);
        setRecaptchaToken(null);
        setSelectedMethod("card");
      } else {
        console.log("Failed to delete card!", data);
      }
    } catch (error) {
      console.log("Delete card error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnectPayPal = async () => {
    if (!window.confirm("Are you sure you want to disconnect your PayPal account?")) return;

    if (setOpenCardDialog) {
      setOpenCardDialog(false);
    }

    setIsLoading(true);

    try {
      const user_id = localStorage.getItem("logged_user_id");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/delete_client_payment_token`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id }),
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success("Your Payment Method has been Deleted successfully from Chef Republic");
        setPaypalAccount(null);
        setCard(null);
        setHasExistingPayment(false);
        setRecaptchaToken(null);
        setSelectedMethod("card");
      } else {
        console.log("Failed to disconnect PayPal account!", data);
      }
    } catch (error) {
      console.log("Disconnect PayPal error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayPalSuccess = async (vaultToken) => {
    setPaypalError(null);
    try {
      const userId = localStorage.getItem("logged_user_id");
      if (!userId) {
        console.log("User ID not found, please login again.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/getCustomerEmail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      const data = await response.json();

      if (data.status) {
        const paypalAccountDetails = {
          id: vaultToken,
          type: "paypal",
          email: data.email || data.data?.email,
          name: data.name?.full_name || data.data?.name || "",
          payment_method_type: "paypal",
        };

        setPaypalAccount(paypalAccountDetails);
        setHasExistingPayment(true);
        setSelectedMethod("paypal");
        toast.success("Your PayPal Account has been saved successfully!");
        fetchPaymentMethods(); // Refresh to ensure correct state

        if (onPaymentSaved) {
          onPaymentSaved(paypalAccountDetails);
        }
        if (onClose) onClose();
        if (setOpenCardDialog) setOpenCardDialog(false);
      } else {
        console.log(data.message || "Failed to fetch PayPal account email");
      }
    } catch (error) {
      console.error("Error fetching PayPal email:", error);
      console.log("Something went wrong while connecting PayPal account");
    }
  };

  const handlePayPalError = (error) => {
    console.error("PayPal error:", error);
    setPaypalError(error.message || "Failed to save PayPal account");
    toast.error("Failed to save PayPal account. Please try again.");
  };

  const handleMethodChange = (method) => {
    if (hasExistingPayment &&
        ((method === "paypal" && card) || (method === "card" && paypalAccount))) {
      toast.success("Please delete your existing payment method first");
      return;
    }
    setSelectedMethod(method);
  };

  const formik = useFormik({
    initialValues: {
      cardNumber: "",
      expire_month: "",
      expire_year: "",
      cvv: "",
      first_name: "",
      last_name: "",
      address: "",
      city: "",
      state: "",
      postal_code: "",
      country_code: "US",
    },
    validationSchema: Yup.object({
      cardNumber: Yup.string().required("Required"),
      expire_month: Yup.string()
        .required("Required")
        .matches(/^(0[1-9]|1[0-2])$/, "Invalid month"),
      expire_year: Yup.string()
        .required("Required")
        .matches(/^[0-9]{4}$/, "Invalid year"),
      cvv: Yup.string()
        .required("Required")
        .matches(/^[0-9]{3,4}$/, "Invalid CVV"),
      first_name: Yup.string().required("Required"),
      last_name: Yup.string().required("Required"),
      address: Yup.string().required("Required"),
      city: Yup.string().required("Required"),
      state: Yup.string().required("Required"),
      postal_code: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      if (hasExistingPayment) {
        toast.success("Please delete your existing payment method first");
        return;
      }

      try {
        const cardDetails = {
          number: values.cardNumber.replace(/\s/g, ""),
          expire_year: values.expire_year,
          expire_month: values.expire_month.padStart(2, "0"),
          cvv2: values.cvv,
          first_name: values.first_name,
          last_name: values.last_name,
          billing_address: {
            address: values.address,
            city: values.city,
            state: values.state,
            postal_code: values.postal_code,
            country_code: "US",
          },
          type:
            detectCardType(values.cardNumber.replace(/\s/g, ""))?.name || null,
          user_id: localStorage.getItem("logged_user_id"),
        };

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}user/add_card_to_vault`,
          {
            method: "POST",
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
            body: JSON.stringify(cardDetails),
          }
        );

        const result = await response.json();
        if (result.status) {
          toast.success("Your Card has been saved successfully!");
          fetchPaymentMethods();
          formik.resetForm();
          if (onPaymentSaved) {
            onPaymentSaved(cardDetails);
          }
          if (onClose) onClose();
          if (setOpenCardDialog) setOpenCardDialog(false);
        } else {
          console.log(result.message || "Failed to save card");
        }
      } catch (error) {
        console.log("Failed to save card");
      }
    },
  });

  // Show spinner while loading payment details
  if (isLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "white",
          zIndex: 9999,
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
      <Box
      sx={{
        maxWidth: 450,
        width: "100%",
        mx: "auto",
        p: 0.5,
        bgcolor: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
          Save Payment Method
        </Typography>
      </Box>

      {/* Payment Options */}
      <Box sx={{ mb: 1 }}>
        {/* PayPal Option */}
        <Paper
          variant="outlined"
          onClick={() => handleMethodChange("paypal")}
          sx={{
            p: 1,
            mb: 1.5,
            cursor: hasExistingPayment && card ? "not-allowed" : "pointer",
            bgcolor: selectedMethod === "paypal" ? "#f5f9fc" : "transparent",
            opacity: hasExistingPayment && card ? 0.6 : 1,
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Radio
              checked={selectedMethod === "paypal"}
              sx={{ p: 0.5 }}
              disabled={hasExistingPayment && card}
            />
            <Box>
              <Typography sx={{ fontSize: "0.95rem" }}>PayPal</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.85rem" }}
              >
                Save PayPal for future payments
              </Typography>
            </Box>
            <Chip
              label="Recommended"
              color="primary"
              size="small"
              sx={{ ml: "auto", height: "20px", fontSize: "0.75rem" }}
            />
          </Box>
        </Paper>

        {/* Card Option */}
        <Paper
          variant="outlined"
          onClick={() => handleMethodChange("card")}
          sx={{
            p: 1.5,
            cursor:
              hasExistingPayment && paypalAccount ? "not-allowed" : "pointer",
            border:
              selectedMethod === "card"
                ? "2px solid #0070ba"
                : "1px solid #e0e0e0",
            bgcolor: selectedMethod === "card" ? "#f5f9fc" : "transparent",
            opacity: hasExistingPayment && paypalAccount ? 0.6 : 1,
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Radio
              checked={selectedMethod === "card"}
              sx={{ p: 0.5 }}
              disabled={hasExistingPayment && paypalAccount}
            />
            <Box>
              <Typography sx={{ fontSize: "0.95rem" }}>
                Credit/Debit Card
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.85rem" }}
              >
                Save card for future payments
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* PayPal Section */}
      {selectedMethod === "paypal" && !paypalAccount && (
        <Box sx={{ mb: 3 }}>
          {paypalError && (
            <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
              {paypalError}
            </Typography>
          )}
          <PaypalVaultSection
            onPaymentSaved={handlePayPalSuccess}
            onError={handlePayPalError}
          />
        </Box>
      )}

      {/* PayPal Account Preview + Disconnect (when PayPal exists) */}
      {paypalAccount && selectedMethod === "paypal" && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: "15px",
              background: "linear-gradient(135deg, #0070ba, #154484)",
              color: "white",
              mb: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <img
                src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
                alt="PayPal"
                style={{ height: "20px", marginRight: "10px" }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Connected PayPal Account
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.8rem", opacity: 0.8 }}>
                  PayPal Email
                </Typography>
                <Typography sx={{ fontWeight: 610 }}>
                  {paypalAccount.email ||
                    paypalAccount.payer_email ||
                    "No email available"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Chip
                  label="Connected"
                  color="success"
                  size="small"
                  sx={{ color: "white", backgroundColor: "#28a745" }}
                />
              </Box>
            </Box>
          </Paper>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
            />
            <Button
              variant="outlined"
              onClick={handleDisconnectPayPal}
              disabled={!recaptchaToken}
              sx={{
                mt: 2,
                color: "white",
                backgroundColor: "#FF5E41",
                borderRadius: "50px",
                padding: "8px 24px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#ff4425" },
                "&.Mui-disabled": {
                  backgroundColor: "#FF5E41",
                  color: "white",
                  opacity: 0.7,
                },
              }}
            >
              Disconnect PayPal
            </Button>
          </Box>
        </Box>
      )}

      {/* Card Form (only when no card exists and no existing payment) */}
      {selectedMethod === "card" && !card && !hasExistingPayment && (
        <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
          <div className="mb-1">
            <input
              placeholder="Card Number"
              type="text"
              name="cardNumber"
              maxLength={19}
              className="form-control shadow-none"
              style={{
                borderRadius: "12px",
                backgroundColor: "rgba(128, 128, 128,0.2)",
                fontSize: "0.92rem",
                height: "32px",
                padding: "2px 10px",
              }}
              value={formik.values.cardNumber}
              onChange={formik.handleChange}
            />
            {formik.touched.cardNumber && formik.errors.cardNumber && (
              <div className="error-msg">{formik.errors.cardNumber}</div>
            )}
          </div>
          <div className="row">
            <div className="col-6 mb-2">
              <input
                placeholder="Month (MM)"
                type="text"
                name="expire_month"
                maxLength={2}
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.expire_month}
                onChange={formik.handleChange}
              />
              {formik.touched.expire_month && formik.errors.expire_month && (
                <div className="error-msg">{formik.errors.expire_month}</div>
              )}
            </div>
            <div className="col-6 mb-2">
              <input
                placeholder="Year (YYYY)"
                type="text"
                name="expire_year"
                maxLength={4}
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.expire_year}
                onChange={formik.handleChange}
              />
              {formik.touched.expire_year && formik.errors.expire_year && (
                <div className="error-msg">{formik.errors.expire_year}</div>
              )}
            </div>
          </div>
          <div className="mb-1">
            <input
              placeholder="CVV"
              type="password"
              name="cvv"
              maxLength={3}
              className="form-control shadow-none"
              style={{
                borderRadius: "12px",
                backgroundColor: "rgba(128, 128, 128,0.2)",
                fontSize: "0.92rem",
                height: "32px",
                padding: "2px 10px",
              }}
              value={formik.values.cvv}
              onChange={formik.handleChange}
            />
            {formik.touched.cvv && formik.errors.cvv && (
              <div className="error-msg">{formik.errors.cvv}</div>
            )}
          </div>
          <div className="row">
            <div className="col-6 mb-2">
              <input
                placeholder="First Name"
                type="text"
                name="first_name"
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.first_name}
                onChange={formik.handleChange}
              />
              {formik.touched.first_name && formik.errors.first_name && (
                <div className="error-msg">{formik.errors.first_name}</div>
              )}
            </div>
            <div className="col-6 mb-2">
              <input
                placeholder="Last Name"
                type="text"
                name="last_name"
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.last_name}
                onChange={formik.handleChange}
              />
              {formik.touched.last_name && formik.errors.last_name && (
                <div className="error-msg">{formik.errors.last_name}</div>
              )}
            </div>
          </div>
          <div className="mb-1">
            <input
              placeholder="Address"
              type="text"
              name="address"
              className="form-control shadow-none"
              style={{
                borderRadius: "12px",
                backgroundColor: "rgba(128, 128, 128,0.2)",
                fontSize: "0.92rem",
                height: "32px",
                padding: "2px 10px",
              }}
              value={formik.values.address}
              onChange={formik.handleChange}
            />
            {formik.touched.address && formik.errors.address && (
              <div className="error-msg">{formik.errors.address}</div>
            )}
          </div>
          <div className="row">
            <div className="col-6 mb-2">
              <input
                placeholder="City"
                type="text"
                name="city"
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.city}
                onChange={formik.handleChange}
              />
              {formik.touched.city && formik.errors.city && (
                <div className="error-msg">{formik.errors.city}</div>
              )}
            </div>
            <div className="col-6 mb-2">
              <input
                placeholder="State"
                type="text"
                name="state"
                className="form-control shadow-none"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(128, 128, 128,0.2)",
                  fontSize: "0.92rem",
                  height: "32px",
                  padding: "2px 10px",
                }}
                value={formik.values.state}
                onChange={formik.handleChange}
              />
              {formik.touched.state && formik.errors.state && (
                <div className="error-msg">{formik.errors.state}</div>
              )}
            </div>
          </div>
          <div className="mb-1">
            <input
              placeholder="Postal Code"
              type="text"
              name="postal_code"
              className="form-control shadow-none"
              style={{
                borderRadius: "12px",
                backgroundColor: "rgba(128, 128, 128,0.2)",
                fontSize: "0.92rem",
                height: "32px",
                padding: "2px 10px",
              }}
              value={formik.values.postal_code}
              onChange={formik.handleChange}
            />
            {formik.touched.postal_code && formik.errors.postal_code && (
              <div className="error-msg">{formik.errors.postal_code}</div>
            )}
          </div>
          <div className="mb-1">
            <input
              placeholder="Country"
              type="text"
              name="country_code"
              value="United States"
              readOnly
              className="form-control shadow-none"
              style={{
                borderRadius: "12px",
                backgroundColor: "rgba(128, 128, 128,0.08)",
                textAlign: "center",
                fontSize: "0.92rem",
                color: "#000",
                height: "32px",
                padding: "2px 10px",
              }}
            />
          </div>

          <div className=" d-flex justify-content-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
            />
          </div>
          <div className="mb-1">
            <button
              type="submit"
              className="btn-orange w-100"
              style={{
                fontSize: "0.98rem",
                height: "36px",
                borderRadius: "18px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              disabled={!recaptchaToken}
            >
              Save Card
            </button>
          </div>
        </form>
      )}

      {/* Card Preview + Delete (when card exists) */}
      {card && selectedMethod === "card" && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: "15px",
              background: "linear-gradient(135deg, #081987ff, #081987ff)",
              color: "white",
              mb: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={
                card?.type === "visa"
                  ? "https://img.icons8.com/color/48/000000/visa.png"
                  : card?.type === "mastercard"
                  ? "https://img.icons8.com/color/48/000000/mastercard.png"
                  : card?.type === "amex"
                  ? "https://img.icons8.com/color/48/000000/amex.png"
                  : card?.type === "discover"
                  ? "https://img.icons8.com/color/48/000000/discover.png"
                  : "https://img.icons8.com/color/48/000000/credit-card.png"
              }
              alt="Card"
              style={{ position: "absolute", top: 15, right: 15 }}
            />
            <Typography variant="h6" sx={{ letterSpacing: 2, mb: 1 }}>
              {`**** **** **** ${(
                card?.last_digits ||
                card?.card_number ||
                card?.number ||
                ""
              )
                .toString()
                .replace(/\s+/g, "")
                .slice(-4)}`}
            </Typography>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.8rem", opacity: 0.8 }}>
                  Card Holder
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {card.first_name ||
                    card.firstName ||
                    card.holder_name ||
                    "Card Holder"}{" "}
                  {card.last_name || card.lastName || ""}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.8rem", opacity: 0.8 }}>
                  Expires
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {card.expire_month ||
                    card.expMonth ||
                    card.expiry_month ||
                    "MM"}
                  /
                  {card.expire_year ||
                    card.expYear ||
                    card.expiry_year ||
                    "YYYY"}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
            />
            <Button
              variant="outlined"
              onClick={handleDeleteCard}
              disabled={!recaptchaToken}
              sx={{
                mt: 2,
                color: "white",
                backgroundColor: "#FF5E41",
                borderRadius: "50px",
                padding: "8px 24px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#ff4425" },
                "&.Mui-disabled": {
                  backgroundColor: "#FF5E41",
                  color: "white",
                  opacity: 0.7,
                },
              }}
            >
              Delete Card
            </Button>
          </Box>
        </Box>
      )}

      {/* PayPal Secure Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          pt: 0,
          mt: "0",
          borderTop: "1px solid #eee",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#666",
            fontSize: "0.8rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Securely processed by
          <img
            src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
            alt="PayPal"
            style={{ height: "16px" }}
          />
        </Typography>
      </Box>
    </Box>
  );
}